import { useEffect, useMemo, useState } from 'react'
import { Box, Breadcrumbs, Typography, Stack, Button, Chip, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, TextField, InputAdornment, Snackbar, Alert } from '@mui/material'
import { Add, MoreVert, Edit, Block, Search } from '@mui/icons-material'
import { DataGrid, type GridColDef } from '@mui/x-data-grid'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { listArticles, deactivateArticle, type ArticleListItem } from '../../api/nomenclatoare'
import { errorMessage } from '../../api/client'
import ArticleFormDialog from './ArticleFormDialog'

const TYPE_LABELS: Record<string, string> = {
  ProdusFinit: 'Produs finit', Semifabricat: 'Semifabricat', MateriePrima: 'Materie primă',
  Ambalaj: 'Ambalaj', Serviciu: 'Serviciu', Set: 'Set',
}

export default function ArticlesPage() {
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300)
    return () => clearTimeout(t)
  }, [search])
  const [formTarget, setFormTarget] = useState<ArticleListItem | null | 'new'>(null)
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; row: ArticleListItem } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const qc = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['nomenclatoare', 'articole', 'lista', debouncedSearch],
    queryFn: () => listArticles(debouncedSearch, false),
  })

  const closeMenu = () => setMenuAnchor(null)

  const deactivateMutation = useMutation({
    mutationFn: deactivateArticle,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['nomenclatoare', 'articole'] }),
    onError: (e) => setError(errorMessage(e)),
  })

  const columns = useMemo<GridColDef<ArticleListItem>[]>(() => [
    { field: 'code', headerName: 'Cod', width: 150, renderCell: (p) => <code>{p.row.code}</code> },
    { field: 'shortDescription', headerName: 'Descriere', flex: 1.4, minWidth: 220 },
    { field: 'type', headerName: 'Tip', width: 140, renderCell: (p) => TYPE_LABELS[p.row.type] ?? p.row.type },
    { field: 'family', headerName: 'Familie', width: 120 },
    { field: 'primaryUnitOfMeasureCode', headerName: 'UM', width: 90 },
    {
      field: 'standardCost', headerName: 'Cost standard', width: 130, type: 'number',
      renderCell: (p) => p.row.standardCost != null ? p.row.standardCost.toFixed(2).replace('.', ',') : '—',
    },
    {
      field: 'status', headerName: 'Status', width: 100, sortable: false,
      renderCell: (p) => p.row.isActive
        ? <Chip label="Activ" size="small" color="success" variant="outlined" />
        : <Chip label="Inactiv" size="small" variant="outlined" />,
    },
    {
      field: 'actions', headerName: '', width: 56, sortable: false, filterable: false, disableColumnMenu: true,
      renderCell: (p) => (
        <IconButton size="small" onClick={(e) => setMenuAnchor({ el: e.currentTarget, row: p.row })}>
          <MoreVert fontSize="small" />
        </IconButton>
      ),
    },
  ], [])

  return (
    <Stack spacing={2}>
      <Breadcrumbs separator="/" sx={{ fontSize: 12.5 }}>
        <Typography variant="inherit" color="text.secondary">Nomenclatoare</Typography>
        <Typography variant="inherit" color="text.primary">Articole</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
        <Typography variant="h2">Articole</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => setFormTarget('new')}>Adaugă articol</Button>
      </Box>

      <TextField
        size="small" placeholder="Caută după cod, cod vechi sau descriere…" value={search}
        onChange={(e) => setSearch(e.target.value)} sx={{ maxWidth: 360 }}
        slotProps={{ input: { startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> } }}
      />

      <Box sx={{ height: 560, bgcolor: 'background.paper', borderRadius: 1.5 }}>
        <DataGrid
          rows={data ?? []} columns={columns} loading={isLoading} density="comfortable"
          disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          pageSizeOptions={[25, 50]}
        />
      </Box>

      <ArticleFormDialog article={formTarget} onClose={() => setFormTarget(null)} />

      <Menu anchorEl={menuAnchor?.el} open={!!menuAnchor} onClose={closeMenu}>
        {menuAnchor && [
          <MenuItem key="edit" onClick={() => { setFormTarget(menuAnchor.row); closeMenu() }}>
            <ListItemIcon><Edit fontSize="small" /></ListItemIcon><ListItemText>Editare</ListItemText>
          </MenuItem>,
          <MenuItem key="deactivate" disabled={!menuAnchor.row.isActive}
            onClick={() => { closeMenu(); deactivateMutation.mutate(menuAnchor.row.id) }}>
            <ListItemIcon><Block fontSize="small" /></ListItemIcon><ListItemText>Dezactivare</ListItemText>
          </MenuItem>,
        ]}
      </Menu>

      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
      </Snackbar>
    </Stack>
  )
}
